import { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useActionGateway } from './hooks/useActionGateway';
import { useNotificationsGateway } from './hooks/useNotificationGateway';
import { useVitalsGateway } from './hooks/useVitalsGateway';
import { Loader } from '@/components/Loader';
import { useAppointmentsStore } from '@/stores/appointments';
import { useInstitutionsStore } from '@/stores/institutions';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { useUsersStore } from '@/stores/users';
import { EUserRoles } from '@/types';
import {
  ESocketListenMessageType,
  ESocketSendMessageType,
} from '@/types/socket';
import { useProvisioningGateway } from './hooks/useProvisioningGateway';

export const SocketLayout = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();

  const { currentUserId, updateUser, setAvailability } = useUsersStore(
    (state) => ({
      currentUserId: state.userId,
      updateUser: state.updateUser,
      setAvailability: state.setAvailability,
    }),
  );
  const currentInstitutionId = useInstitutionsStore(
    (state) => state.currentInstitution?.id,
  );

  const setToast = useToastStore((state) => state.setToast);

  const isCoordinator = useUsersStore((state) =>
    state.checkRole(EUserRoles.COORDINATOR),
  );

  const {
    connectSocket,
    disconnectSocket,
    sendMessage,
    listenMessage,
    stopListenMessage,
    isConnected,
    hasConnectedOnce,
  } = useSocketStore((state) => ({
    connectSocket: state.connectSocket,
    disconnectSocket: state.disconnectSocket,
    sendMessage: state.sendMessage,
    listenMessage: state.listenMessage,
    stopListenMessage: state.stopListenMessage,
    isConnected: state.isConnected,
    hasConnectedOnce: state.hasConnectedOnce,
  }));
  const { getAppointmentById, updateAppointment } = useAppointmentsStore(
    (state) => ({
      getAppointmentById: state.getAppointmentById,
      updateAppointment: state.updateAppointment,
    }),
  );
  const updateInvite = useUsersStore((state) => state.updateInvite);

  useEffect(() => {
    connectSocket();
    return () => disconnectSocket();
  }, [connectSocket, disconnectSocket]);

  // ***** START/STOP EVENT SUBSCRIPTIONS *****
  useEffect(() => {
    if (!isConnected || !currentInstitutionId) return;

    sendMessage({
      type: ESocketSendMessageType.FIREHOSE_START,
      body: { institutionId: currentInstitutionId },
    });

    return () => {
      sendMessage({
        type: ESocketSendMessageType.FIREHOSE_STOP,
        body: { institutionId: currentInstitutionId },
      });
    };
  }, [currentInstitutionId, isConnected, sendMessage]);

  // ***** LISTEN TO EVENTS *****

  useEffect(() => {
    if (!isConnected) return;

    listenMessage({
      type: ESocketListenMessageType.APPOINTMENT,
      callback: ({ appointment }) => {
        if (!appointment || !Object.keys(appointment).length) return;
        updateAppointment(appointment);
      },
    });

    listenMessage({
      type: ESocketListenMessageType.INVITE,
      callback: ({ invite }) => {
        if (!invite || !Object.keys(invite).length) return;
        updateInvite(invite);
      },
    });

    listenMessage({
      type: ESocketListenMessageType.USER,
      callback: ({ user }) => {
        if (!user) return;
        if (currentUserId === user.id) {
          setAvailability(user.availability);
        }
        // The firehose broadcasts presence updates for anyone connected to the
        // institution's socket room, including super/org admins who are just
        // viewing it and hold no role there. Only merge users who actually
        // belong to this institution, or a stray viewer gets inserted into the
        // users table until the next fetch filters them back out.
        const belongsToCurrentInstitution = user.userInstitutionRoles.some(
          ({ institutionId }) => institutionId === currentInstitutionId,
        );
        if (belongsToCurrentInstitution) {
          updateUser(user);
        }
      },
    });
    return () => {
      stopListenMessage(ESocketListenMessageType.APPOINTMENT);
      stopListenMessage(ESocketListenMessageType.INVITE);
      stopListenMessage(ESocketListenMessageType.USER);
    };
  }, [
    currentUserId,
    currentInstitutionId,
    isConnected,
    updateUser,
    listenMessage,
    getAppointmentById,
    updateAppointment,
    stopListenMessage,
    isCoordinator,
    t,
    setToast,
    setAvailability,
    updateInvite,
  ]);

  useNotificationsGateway();
  useProvisioningGateway();
  useActionGateway();
  useVitalsGateway();

  // Only show loader on initial connection, not on reconnection
  if (!isConnected && !hasConnectedOnce) {
    return <Loader full />;
  }

  return children;
};
